'use client'

import { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { FileBoxIcon, SaveIcon, XIcon } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Progress } from '@/components/ui/progress'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'
import { UnsavedChangesGuardDialog } from '@/components/partials'
import { setValidationErrors } from '@/lib/utils'
import { CreateFileParams, FileType } from '@/types/file'
import { AccessLevelEnum, FormModeEnum } from '@/enums'

type PropsType = {
  onSubmit: (data: CreateFileParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
  uploadProgress?: number
  initialFile?: FileType
  optimizeImageSelection: boolean
  types?: string[]
}

/**
 * Whether a file matches an allowed MIME-type list.
 * An empty list means "no restriction". Supports `type/*` wildcards (e.g. `image/*`).
 */
const isAllowedType = (file: File, allowed: string[]): boolean => {
  if (allowed.length === 0) {
    return true
  }
  return allowed.some(type => (type.endsWith('/*') ? file.type.startsWith(type.slice(0, -1)) : file.type === type))
}

export interface FileFormRef {
  handleValidationErrors: (error: any) => void
}

const FileForm = forwardRef<FileFormRef, PropsType>(
  (
    { onSubmit, onCancel, isLoading = false, mode, uploadProgress, initialFile, optimizeImageSelection, types = [] },
    ref
  ) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [isImageFile, setIsImageFile] = useState<boolean>(false)
    const [hasNewFileSelected, setHasNewFileSelected] = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const formRef = useRef<HTMLDivElement>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    // Create schema based on mode
    const schema = z.object({
      file:
        mode === FormModeEnum.UPDATE
          ? z
              .instanceof(FileList)
              .nullable()
              .optional()
              .refine(files => !files?.[0] || isAllowedType(files[0], types), t('invalid_file_type'))
              .transform(files => (files && files.length > 0 ? files[0] : undefined))
          : z
              .instanceof(FileList)
              .refine(files => files && files.length > 0, t('required'))
              .refine(files => !files?.[0] || isAllowedType(files[0], types), t('invalid_file_type'))
              .transform(files => files && files[0]),
      name: z.string().min(1, t('not_blank')),
      optimize: z.boolean().default(false),
      accessLevel: z
        .enum([AccessLevelEnum.PUBLIC, AccessLevelEnum.USER, AccessLevelEnum.ADMIN])
        .default(AccessLevelEnum.PUBLIC)
    })

    const form = useForm({
      resolver: zodResolver(schema),
      defaultValues: {
        file: null,
        name: initialFile?.name || '',
        optimize: false,
        accessLevel: initialFile?.accessLevel || AccessLevelEnum.PUBLIC
      }
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    // Initialize a form with file data when in edit mode
    useEffect(() => {
      if (mode === FormModeEnum.UPDATE && initialFile) {
        form.setValue('name', initialFile.name)
        // Do not infer optimizing or image state from an existing file
        setIsImageFile(false)
        setHasNewFileSelected(false)
        form.setValue('optimize', false)
      }
    }, [initialFile, form, mode])

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      }
    }))

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        const file = files[0]

        // Reject files whose MIME type is not in the restricted allow-list (e.g. non-mp4 videos)
        if (!isAllowedType(file, types)) {
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
          form.setValue('file', null, { shouldValidate: false })
          form.setError('file', { type: 'manual', message: t('invalid_file_type') })
          setPreviewUrl(null)
          setIsImageFile(false)
          setHasNewFileSelected(false)
          return
        }

        form.clearErrors('file')
        form.setValue('file', files)
        setHasNewFileSelected(true)

        if (!form.getValues('name')) {
          const fileName = file.name.split('.')[0]
          form.setValue('name', fileName)
        }

        // Check if it's an image but exclude SVG files from optimization
        const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')

        if (file.type.startsWith('image/')) {
          setIsImageFile(!isSvg) // SVG files should not be treated as optimizable images
          form.setValue('optimize', !isSvg) // Don't optimize SVG files
          const reader = new FileReader()
          reader.onload = () => {
            setPreviewUrl(reader.result as string)
          }
          reader.readAsDataURL(file)
        } else {
          setIsImageFile(false)
          form.setValue('optimize', false)
          setPreviewUrl(null)
        }
      }
    }

    const handleSubmit = (data: any) => {
      // In edit mode, we can submit without a file
      if (mode === FormModeEnum.UPDATE || data.file) {
        setIsSubmitting(true)
        onSubmit({
          ...(data.file && { file: data.file }),
          name: data.name,
          optimize: data.optimize,
          accessLevel: data.accessLevel
        })
      }
    }

    const handleClearFile = () => {
      // Clear the actual input element's value (so the same file can be re-selected)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Restore the name from initial data if available (edit mode), otherwise keep the current name
      const nameToRestore = initialFile?.name || ''
      form.setValue('name', nameToRestore)

      // Clear RHF state for file and related UI flags
      form.setValue('file', null, { shouldValidate: true, shouldDirty: true })
      form.clearErrors('file')
      form.setValue('optimize', false)
      setPreviewUrl(null)
      setIsImageFile(false)
      setHasNewFileSelected(false)
    }

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileBoxIcon className="h-5 w-5" />
              {mode === FormModeEnum.CREATE ? t('create_file') : t('edit_file')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <div ref={formRef as any} className="space-y-4">
                <div className="grid grid-cols-1 items-start gap-0 lg:grid-cols-2 lg:gap-4 xl:grid-cols-4">
                  <FormField
                    control={form.control}
                    name="file"
                    render={({ field: { onChange: _onChange, value: _value, ...field } }) => (
                      <div className="relative pr-12">
                        <FormItem>
                          <FormLabel required>{t('file')}</FormLabel>
                          <FormControl>
                            <div className="flex flex-col space-y-2">
                              <Input
                                type="file"
                                accept={
                                  types.length > 0 ? types.join(',') : 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar'
                                }
                                {...field}
                                onChange={handleFileChange}
                                ref={el => {
                                  fileInputRef.current = el
                                  ;(field as any).ref?.(el)
                                }}
                              />
                              {previewUrl && (
                                <div className="mt-2 flex justify-start">
                                  <div className="bg-checkerboard inline-flex items-center justify-center rounded border border-amber-400 p-0.5">
                                    <Image
                                      src={previewUrl}
                                      alt={t('preview')}
                                      width={200}
                                      height={150}
                                      className="object-contain"
                                    />
                                  </div>
                                </div>
                              )}
                              {isLoading &&
                                uploadProgress !== undefined &&
                                uploadProgress > 0 &&
                                uploadProgress < 100 && (
                                  <div className="mt-2">
                                    <div className="mb-1 flex justify-between">
                                      <span className="text-sm font-medium">
                                        {uploadProgress >= 90 ? t('processing') : t('uploading')}
                                      </span>
                                      <span className="text-sm font-medium">{uploadProgress}%</span>
                                    </div>
                                    <Progress value={uploadProgress} className="w-full" />
                                  </div>
                                )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                        <Button
                          variant="destructive"
                          type="button"
                          className="absolute top-5.5 right-0"
                          onClick={handleClearFile}
                          disabled={!form.getValues('file')}>
                          <XIcon size="12" />
                        </Button>
                      </div>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>{t('name')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('name')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="accessLevel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('access_level')}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl className="w-full">
                            <SelectTrigger>
                              <SelectValue placeholder={t('select')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={AccessLevelEnum.PUBLIC}>{t('public')}</SelectItem>
                            <SelectItem value={AccessLevelEnum.USER}>{t('user')}</SelectItem>
                            <SelectItem value={AccessLevelEnum.ADMIN}>{t('admin')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="optimize"
                    render={({ field }) => (
                      <FormItem className="mt-0 flex flex-row items-start lg:mt-8">
                        <FormControl>
                          <Checkbox
                            checked={field.value && optimizeImageSelection}
                            onCheckedChange={field.onChange}
                            disabled={!(hasNewFileSelected && isImageFile)}
                          />
                        </FormControl>
                        <FormLabel>{t('optimize_image')}</FormLabel>
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between gap-3 pt-1">
                  <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading || isSubmitting}>
                    <XIcon />
                    {t('cancel')}
                  </Button>
                  <Button
                    type="button"
                    onClick={form.handleSubmit(handleSubmit, scrollToFirstError)}
                    disabled={isLoading || isSubmitting}>
                    <SaveIcon />
                    {isLoading || isSubmitting ? t('saving') : t(mode)}
                  </Button>
                </div>
              </div>
            </Form>
          </CardContent>
        </Card>
        <UnsavedChangesGuardDialog
          open={isNavigationBlocked}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      </div>
    )
  }
)

FileForm.displayName = 'FileForm'

export default FileForm
